import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiCartAlt } from 'react-icons/bi';
import ReactGA from 'react-ga4';
import {
    PaymentElement,
    useElements,
    useStripe
} from '@stripe/react-stripe-js';
import moment from 'moment';
import CheckoutVector from 'assets/vectors/Checkout.svg';
import axios from 'config/axios';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { initializeCart } from 'store/cart';
import { setAlert } from 'store/error';
import './index.css';

function CheckoutForm({
    total,
    paymentId,
    updateTotal
}: { 
    total: number;
    paymentId: string;
    updateTotal: React.Dispatch<React.SetStateAction<number>>;
}) {
    const [applyingCode, setApplyingCode] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoCodeName, setPromoCodeName] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);

    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.user);
    const {
        bundles,
        courses,
        tests,
        promoCode: code
    } = useAppSelector(state => state.cart);

    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (total < 0.5) {
            axios.post('payments/transaction/', {
                    bundles: bundles.map(bundle => bundle.id),
                    courses: courses.map(course => course.urlSlug),
                    tests: tests.map(test => test.testId),
                    paymentId: paymentId,
                    paymentGateway: 'STRIPE',
                    paymentAmount: total,
                    discounts: [
                        ...bundles.map(bundle => bundle.discount?.name),
                        ...courses.map(course => course.discount?.name),
                        ...tests.map(test => test.discount?.name)
                    ],
                    promoCode: code
                })
                .then(res => {
                    const { orderNumber } = res.data;

                    // ReactGA.event('purchase', {
                    //     currency: 'USD',
                    //     transaction_id: orderNumber,
                    //     value: total,
                    //     coupon: code,
                    //     items: [
                    //         ...bundles.filter(bundle => (
                    //             !bundle.numRequired
                    //         )).reduce((bundleItemList, bundle) => (
                    //             [
                    //                 ...bundleItemList,
                    //                 {
                    //                     item_id: bundle.id,
                    //                     item_name: bundle.name,
                    //                     price: bundle.courses.reduce((total, course) => (
                    //                             total + course.tuition
                    //                         ), 0) + bundle.tests.reduce((total, test) => (
                    //                             total + test.price
                    //                         ), 0),
                    //                     discount: bundle.discount?.type === 'Percent'
                    //                         ?   (bundle.courses.reduce((total, course) => (
                    //                                 total + course.tuition
                    //                             ), 0) + bundle.tests.reduce((total, test) => (
                    //                                 total + test.price
                    //                             ), 0)) * bundle.discount.value / 100
                    //                         :   bundle.discount?.type === 'Amount'
                    //                         ?   bundle.discount?.value
                    //                         :   0
                    //                 }
                    //             ]
                    //         ), []),
                    //         ...courses.map(course => ({
                    //             item_id: course.urlSlug,
                    //             item_name: course.name,
                    //             price: course.tuition,
                    //             discount: course.discount?.type === 'Percent'
                    //                 ?   course.tuition * course.discount.value / 100
                    //                 :   course.discount?.type === 'Amount'
                    //                 ?   course.discount.value
                    //                 :   0
                    //         })),
                    //         ...tests.map(test => ({
                    //             item_id: test.testId,
                    //             item_name: test.name,
                    //             price: test.price,
                    //             discount: test.discount?.type === 'Percent'
                    //                 ?   test.price * test.discount.value / 100
                    //                 :   test.discount?.type === 'Amount'
                    //                 ?   test.discount.value
                    //                 :   0
                    //         }))
                    //     ]
                    // });
                    
                    navigate(`/payment/${orderNumber}`, { replace: true });
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                    
                    // If there is an error registering student, issue a refund
                });
        }

        else {
            if (!stripe || !elements) {
                return;
            }

            const result = await stripe.confirmPayment({
                elements, 
                confirmParams: {
                    receipt_email: user.email,
                    return_url: `${process.env.CLIENT_BASE_URL}/payment/stripe${code ? `?promoCode=${code}` : ''}`
                }
            });

            if (result.error) {
                // console.log(result.error.message);
                dispatch(setAlert({ 
                    response: { 
                        data: { 
                            message: result.error.message
                        }
                    }
                }));
            }
        }
    }

    const applyPromoCode = () => {
        setApplyingCode(true);
        
        axios.post('payments/promoCode/', {
                promoCode: promoCode,
                total: total,
                paymentId: paymentId
            })
            .then(res => {
                const {
                    name,
                    discountAmount,
                    newTotal
                } = res.data;

                // console.log(res.data);

                dispatch(initializeCart({
                    'promoCode': promoCode
                }));
                updateTotal(newTotal);
                setPromoCodeName(name);
                setDiscountAmount(discountAmount);
                setApplyingCode(false);
            })
            .catch(error => {
                dispatch(setAlert(error));
                setPromoCode('');
                setApplyingCode(false);
            });
    }

    const cardContent = <>
        <h2 className='checkout__cardHeader'>
            <BiCartAlt className='checkout__cardHeaderIcon' />
            Order Summary
        </h2>
        {bundles.filter(bundle => bundle.tests.length === 0).map(bundle => (
            <div key={bundle.name} className='checkout__cardItem'>
                <div className='checkout__cardItemHeader'>
                    <h4 className='checkout__cardItemName'>
                        {bundle.name}
                    </h4>
                    {bundle.discount
                    ?   <p className='checkout__cardItemDiscount'>
                            <span>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</span>
                            ${bundle.discount.type === 'Percent'
                                ? (bundle.courses.reduce((total, { tuition }) => total + tuition, 0)*(100 - bundle.discount.value)/100).toFixed(2)
                                : bundle.discount.type === 'Amount'
                                ? (bundle.courses.reduce((total, { tuition }) => total + tuition, 0) - bundle.discount.value).toFixed(2)
                                : bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)
                            }
                        </p>
                    :   <p className='checkout__cardItemPrice'>
                            ${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}
                        </p>
                    }
                </div>
                {bundle.courses.map(course => (
                    <p className='checkout__cardItemRow'>
                        {course.name}
                    </p>
                ))}
            </div>
        ))}
        {courses.map(course => <div key={course.name} className='checkout__cardItem'>
            <div className='checkout__cardItemHeader'>
                <h4 className='checkout__cardItemName'>
                    {course.name}
                </h4>
                {course.discount
                ?   <p className='checkout__cardItemDiscount'>
                        <span>${course.tuition.toFixed(2)}</span>
                        ${course.discount.type === 'Percent'
                            ? (course.tuition - course.discount.value*course.tuition/100).toFixed(2)
                            : course.discount.type === 'Amount'
                            ? (course.tuition - course.discount.value).toFixed(2)
                            : course.tuition.toFixed(2)
                        }
                    </p>
                :   <p className='checkout__cardItemPrice'>${course.tuition.toFixed(2)}</p>
                }
            </div>
            <p className='checkout__cardItemRow'>
            Available for {Math.round(moment.duration(course.period).asMonths())} Months
            </p>
        </div>)}
        {bundles.filter(bundle => bundle.tests?.length > 0).map(bundle => (
            <div key={bundle.name} className='checkout__cardItem'>
                <div className='checkout__cardItemHeader'>
                    <h4 className='checkout__cardItemName'>
                        {bundle.name}
                    </h4>
                    {bundle.discount
                    ?   <p className='checkout__cardItemDiscount'>
                            <span>${bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)}</span>
                            ${bundle.discount.type === 'Percent'
                                ? (bundle.tests.reduce((total, { price }) => total + price, 0)*(100 - bundle.discount.value)/100).toFixed(2)
                                : bundle.discount.type === 'Amount'
                                ? (bundle.tests.reduce((total, { price }) => total + price, 0) - bundle.discount.value).toFixed(2)
                                : bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)
                            }
                        </p>
                    :   <p className='checkout__cardItemPrice'>
                            ${bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)}
                        </p>
                    }
                </div>
                {bundle.tests.map(test => (
                    <p className='checkout__cardItemRow'>
                        {test.name}
                    </p>
                ))}
            </div>
        ))}
        {tests.filter(test => bundles.every(bundle => (
            !bundle.tests.find(bundleTest => bundleTest.testId === test.testId)
        ))).map(test => (

        // {tests.map(test => (
            <div key={test.name} className='checkout__cardItem'>
                <div className='checkout__cardItemHeader'>
                    <h4 className='checkout__cardItemName'>
                        {test.name}
                    </h4>
                    {test.discount
                        ?   <p className='checkout__cardItemDiscount'>
                                <span>${test.price.toFixed(2)}</span>
                                ${test.discount.type === 'Percent'
                                    ? (test.price - test.discount.value*test.price/100).toFixed(2)
                                    : test.discount.type === 'Amount'
                                    ? (test.price - test.discount.value).toFixed(2)
                                    : test.price.toFixed(2)
                                }
                            </p>
                        :   <p className='checkout__cardItemPrice'>${test.price.toFixed(2)}</p>
                    }
                </div>
                <p className='checkout__cardItemRow'>
                    {test.type}
                </p>
            </div>
        ))}
        {promoCodeName
        ?   <div className='checkout__cardItemHeader'>
                <h4 className='checkout__promoCodeName'>
                    {promoCodeName}
                </h4>
                <p className='checkout__promoCodeDiscount'>
                    -${discountAmount.toFixed(2)}
                </p>
            </div>
        :   <div className='checkout__promo'>
                <TextInput 
                    className='checkout__promoCode'
                    name='promoCode'
                    label='promo code'
                    onChange={(event) => setPromoCode(event.target.value)}
                    value={promoCode}
                />
                <Button
                    className='checkout__promoButton'
                    onClick={applyPromoCode}
                    disabled={applyingCode}
                    type='button'
                >
                    apply
                </Button>
            </div>
        }
        
        <div className='checkout__cardTotal'>
            <p>Your Total</p>
            <b>${total.toFixed(2)}</b>
        </div>
    </>;

    return (
        <div className='layout'>
            <div className='checkout'>
                <div className='checkout__card'>
                    {cardContent}
                </div>
                <form className='checkout__form' onSubmit={handleSubmit}>
                    <img
                        className='checkout__vector'
                        src={CheckoutVector}
                        alt='Checkout'
                    />
                    <h1 className='checkout__header'>
                        Payment Information
                    </h1>
                    <div className='checkout__card--inline'>
                        {cardContent}
                    </div>
                    <PaymentElement />
                    <Button
                        className='checkout__button'
                        fullWidth
                        type='submit'
                    >
                        PAY NOW
                    </Button>
                </form>
            </div>
        </div>
    );
}

export default CheckoutForm;